import React, { useState } from 'react';
import { T, fontBody, GlobalStyles } from '../../components/social-media-automation/tokens';
import useSocialAutoReply from '../../components/social-media-automation/hooks/useSocialAutoReply';
import { buildIntegrationCards, createNewAutomationDraft } from '../../components/social-media-automation/utils';
import { TAB_ITEMS } from '../../components/social-media-automation/constants';
import PageHeader from '../../components/social-media-automation/PageHeader';
import AlertBanner from '../../components/social-media-automation/AlertBanner';
import IntegrationCard from '../../components/social-media-automation/IntegrationCard';
import TabBar from '../../components/social-media-automation/TabBar';
import AdvancedSettings from '../../components/social-media-automation/AdvancedSettings';
import TemplateList from '../../components/social-media-automation/templates/TemplateList';
import AutomationFlowModal from '../../components/social-media-automation/templates/AutomationFlowModal';

export default function SocialMediaAutomationPage({ embeddedInFacebook = false, connectedPages = null }) {
  const {
    settings,
    setSettings,
    templates,
    pages,
    loading,
    saving,
    error,
    setError,
    success,
    setSuccess,
    isDirty,
    saveAll,
    toggleTemplateStatus,
    saveTemplate,
    createTemplate,
    deleteTemplate,
    previewTemplate,
  } = useSocialAutoReply({ connectedPages });

  const [view, setView] = useState('list');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [flowModal, setFlowModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // template pending deletion

  const integrations = buildIntegrationCards(pages);

  const handleFlowSave = async (payload) => {
    try {
      if (payload.isNew) {
        const { isNew, id, settingsPatch, ...createPayload } = payload;
        await createTemplate(createPayload);
        setFlowModal(null);
      } else {
        const { isNew, settingsPatch, ...updates } = payload;
        await saveTemplate(payload.id || payload.templateType, { ...updates, settingsPatch });
        setFlowModal(null);
      }
    } catch {
      // error set in hook
    }
  };

  const handleSettingsChange = (patch) => {
    setSettings((s) => ({ ...s, ...patch }));
  };

  const openCreateFlow = () => {
    setFlowModal({ template: createNewAutomationDraft(), isNew: true });
  };

  const openEditFlow = (template) => {
    if (template.templateType === 'comment_reply') {
      // DM body is stored in comment_reply.dm_body (preferred).
      // Fall back to the legacy comment_dm template only if dm_body is still empty.
      const dmBody = template.dmBody
        || templates.find((t) => t.templateType === 'comment_dm')?.body
        || '';
      setFlowModal({ template: { ...template, dmBody }, isNew: false });
    } else {
      setFlowModal({ template, isNew: false });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <div style={{ width: 32, height: 32, border: `3px solid ${T.line}`, borderTopColor: T.blue, borderRadius: '50%', animation: 'smaSpin 0.8s linear infinite' }} />
        <GlobalStyles />
      </div>
    );
  }

  const wrapperStyle = embeddedInFacebook
    ? { width: '100%', fontFamily: fontBody }
    : { background: T.page, minHeight: '100%', fontFamily: fontBody };

  const innerStyle = embeddedInFacebook
    ? {}
    : { maxWidth: 1180, margin: '0 auto', padding: '32px 24px 60px' };

  return (
    <div style={wrapperStyle}>
      <GlobalStyles />
      <div style={innerStyle}>
        <PageHeader
          embedded={embeddedInFacebook}
          saving={saving}
          isDirty={isDirty}
          onSave={saveAll}
          onCreate={view === 'list' ? openCreateFlow : undefined}
          showSave={view === 'settings'}
        />

        <AlertBanner type="error" message={error} onClose={() => setError('')} />
        <AlertBanner type="success" message={success} onClose={() => setSuccess('')} />
        {settings && !settings.enabled && (
          <AlertBanner type="warning" message="Auto-reply is paused. Enable it under Advanced settings." />
        )}

        {!embeddedInFacebook && (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
            {integrations.map((i) => <IntegrationCard key={i.id} integration={i} />)}
          </div>
        )}

        <TabBar tabs={TAB_ITEMS} active={view} onChange={setView} />

        {view === 'list' && (
          <TemplateList
            templates={templates}
            filter={filter}
            search={search}
            onFilterChange={setFilter}
            onSearchChange={setSearch}
            onEdit={openEditFlow}
            onToggleStatus={toggleTemplateStatus}
            onDelete={(tpl) => setDeleteConfirm(tpl)}
            toggling={saving}
          />
        )}

        {view === 'settings' && settings && (
          <AdvancedSettings
            settings={settings}
            onChange={handleSettingsChange}
          />
        )}
      </div>

      {deleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(11,18,32,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1400, padding: 24,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, padding: '28px 28px 24px',
            width: 'min(420px, 100%)', boxShadow: '0 20px 60px -12px rgba(11,18,32,0.35)',
          }}
          >
            <div style={{ fontFamily: fontBody, fontSize: 18, fontWeight: 700, color: '#0B1220', marginBottom: 8 }}>
              Delete automation?
            </div>
            <div style={{ fontFamily: fontBody, fontSize: 14, color: T.slate, lineHeight: 1.6, marginBottom: 24 }}>
              <strong style={{ color: '#0B1220' }}>{deleteConfirm.name}</strong> will be permanently
              deleted and cannot be restored.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={saving}
                onClick={() => setDeleteConfirm(null)}
                style={{
                  fontFamily: fontBody, fontSize: 13, fontWeight: 600,
                  color: T.ink, background: 'transparent', border: `1px solid ${T.line}`,
                  padding: '9px 18px', borderRadius: 9, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  await deleteTemplate(deleteConfirm);
                  setDeleteConfirm(null);
                }}
                style={{
                  fontFamily: fontBody, fontSize: 13, fontWeight: 600,
                  color: '#fff', background: '#DC2626', border: 'none',
                  padding: '9px 18px', borderRadius: 9,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {flowModal && settings && (
        <AutomationFlowModal
          template={flowModal.template}
          isNew={flowModal.isNew}
          settings={settings}
          onClose={() => !saving && setFlowModal(null)}
          onSave={handleFlowSave}
          onPreview={previewTemplate}
          onSettingsChange={handleSettingsChange}
          saving={saving}
          error={error}
          onClearError={() => setError('')}
        />
      )}
    </div>
  );
}
